import { feederCircuitId } from "../feederCircuits";
import {
  DEFAULT_INSTALLATION_HEIGHTS,
  INSTALLATION_HEIGHT_KEYS,
  suggestTrayHeight,
} from "../installationHeights";
import type {
  BuildSetupProgress,
  SetupBoardRow,
  SetupBoardStatus,
  SetupStepState,
  SetupWallRow,
} from "./types";

const stepStates = (confirmed: boolean[]): SetupStepState[] => {
  const activeIndex = confirmed.findIndex((isConfirmed) => !isConfirmed);
  return confirmed.map((isConfirmed, index) =>
    isConfirmed ? "done" : index === activeIndex ? "active" : "upcoming",
  );
};

export const buildSetupProgress: BuildSetupProgress = ({
  boards,
  mainBoard,
  feederCircuits,
  feedPointByCircuit,
  feederOffFloor,
  cableTypeByCircuit,
  storedBoardFeeds,
  areaFeeds,
  storedHeights,
  trayTypes,
  boardsConfirmed,
  heightsConfirmed,
  traysConfirmed,
  walls,
}) => {
  const [boardsState, heightsState, traysState, wallsState] = stepStates([
    boardsConfirmed,
    heightsConfirmed,
    traysConfirmed,
    ...(walls ? [walls.confirmed] : []),
  ]);
  const mainLabel = mainBoard.kind === "answered" || mainBoard.kind === "note" ? mainBoard.label : null;
  const nameOf = (label: string) => boards.find((board) => board.label === label)?.name ?? label;
  const feederByTarget = new Map(Object.values(feederCircuits).map((feeder) => [feeder.toLabel, feeder]));

  const boardRows = boards.map((board): SetupBoardRow => {
    const feederId = feederCircuitId(board.label);
    const feeder = feederByTarget.get(board.label);
    const isOffFloor = Boolean(feederOffFloor[board.label]);
    const isFedFromStored = feedPointByCircuit[feederId] !== undefined;
    const feederCableType = cableTypeByCircuit[feederId] ?? null;
    const heightM = storedHeights.boardHeights?.[board.label] ?? null;
    const feeds = storedBoardFeeds?.[board.label]?.categories ?? [];
    const zoneCount = areaFeeds.filter((area) => area.sourceLabel === board.label).length;
    const hasAnswer =
      isOffFloor || isFedFromStored || feederCableType !== null || heightM !== null || feeds.length > 0 || zoneCount > 0;
    const status: SetupBoardStatus = board.isRemoved
      ? "struck"
      : board.label === mainLabel
        ? "main"
        : hasAnswer
          ? "considered"
          : "open";
    const isLive = status !== "struck";
    return {
      label: board.label,
      name: board.name,
      status,
      fedFromName: isLive && isFedFromStored && feeder ? nameOf(feeder.fromLabel) : null,
      isOffFloor: isLive && isOffFloor,
      feederCableType: isLive ? feederCableType : null,
      heightM: isLive ? heightM : null,
      feeds: isLive ? feeds : [],
      zoneCount: isLive ? zoneCount : 0,
    };
  });
  const statusOrder: Record<SetupBoardStatus, number> = { main: 0, considered: 1, open: 1, struck: 2 };
  const sortedBoards = [...boardRows].sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

  const heightRows = INSTALLATION_HEIGHT_KEYS.map((key) => ({
    key,
    heightM: storedHeights.installationHeights?.[key] ?? null,
    suggestionM: DEFAULT_INSTALLATION_HEIGHTS[key],
  }));
  const levels = { ...DEFAULT_INSTALLATION_HEIGHTS, ...storedHeights.installationHeights };
  const trayRows = trayTypes.map((trayType) => ({
    trayType,
    heightM: storedHeights.trayHeights?.[trayType] ?? null,
    suggestionM: suggestTrayHeight({ trayType, levels }),
  }));

  return {
    boards: {
      key: "boards",
      state: boardsState,
      answeredCount: boardRows.filter((row) => row.status !== "open").length,
      rows: sortedBoards,
    },
    heights: {
      key: "heights",
      state: heightsState,
      answeredCount: heightRows.filter((row) => row.heightM !== null).length,
      rows: heightRows,
    },
    trays: {
      key: "trays",
      state: traysState,
      answeredCount: trayRows.filter((row) => row.heightM !== null).length,
      rows: trayRows,
    },
    ...(walls && wallsState
      ? {
          walls: {
            key: "walls" as const,
            state: wallsState,
            answeredCount: walls.counts.changed,
            rows: (["walls", "doors", "changed"] as const)
              .map((key): SetupWallRow => ({ key, count: walls.counts[key] }))
              .filter((row) => row.key !== "changed" || row.count > 0),
          },
        }
      : {}),
  };
};
